import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { AppSettings } from '../../PRE-LOAD/app-settings';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserSelectionsService } from '../../services/user-selections/user-selections.service';
import { PickupLocation } from '../../types/strings.type';
import { StringsService } from '../../services/strings/strings.service';
import { ErrorService } from '../../services/errors/error.service';
import { Utilities } from '../../utilities/utilities';
import { pairwise, startWith } from 'rxjs/operators';

@Component({
    selector: 'rttd-pickup-location',
    templateUrl: './pickup-location.component.html',
    styles: [
        require('./pickup-location.component.scss').default.toString(),
        require(`./pickup-location.component.${environment.brand}.scss`).default.toString()
    ]
})
export class PickupLocationComponent implements OnInit {

    @Output() isOffSite = new EventEmitter<boolean>();

    content: PickupLocation;
    detailsForm: FormGroup;
    showAddress = false;
    offsiteMessage: string;
    locationOptions = [
        {label: 'At centre', value: this.utilities.startLocationAsString('on')},
        {label: 'Other location', value: this.utilities.startLocationAsString('off')}
    ];
    defaultRadioValue: string;

    // * Autocomplete =>
    // These are work around's for forcing Angular to work with various browsers native autocomplete functionality.
    // The speed of autocomplete filling out the form seems to interfere with the digest cycle of the Angular model.
    // To fix this, we 'manually' enter the data from autocomplete into the model, and also enter it if the user
    // already has data in memory (ie presses a back button).
    forcedInputs = {
        address1: false,
        address2: false,
        address3: false,
        city: false,
        county: false,
        postCode: false
    };

    constructor(
        public appSettings: AppSettings,
        public userSelectionsService: UserSelectionsService,
        private stringsService: StringsService,
        private errorService: ErrorService,
        private utilities: Utilities
    ) {

    }

    ngOnInit() {

        this.stringsService.get().subscribe((data: PickupLocation) => {
            this.content = data['PickupLocation'];
        });

        this.offsiteMessage = this.appSettings.offSiteLocationCopy;

        if (this.userSelectionsService.get('startLocation')) {
            this.defaultRadioValue = this.userSelectionsService.get('startLocation');
        }

        this.detailsForm = new FormGroup({
            address1: new FormControl(this.bruteForceInitialValue('address1'), [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(200)
            ]),
            address2: new FormControl(this.bruteForceInitialValue('address2'), [
                Validators.maxLength(200)
            ]),
            address3: new FormControl(this.bruteForceInitialValue('address3'), [
                Validators.maxLength(200)
            ]),
            city: new FormControl(this.bruteForceInitialValue('city'), [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(75)
            ]),
            county: new FormControl(this.bruteForceInitialValue('county'), [
                Validators.maxLength(75)
            ]),
            postCode: new FormControl(this.bruteForceInitialValue('postCode'), [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(200),
                Validators.pattern('([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\\s?[0-9][A-Za-z]{2})')
            ]),
        }, {updateOn: 'change'});

        // * Autocomplete.
        [
            'address1',
            'address2',
            'address3',
            'city',
            'county',
            'postCode'
        ].forEach((type) => {
            this.detailsForm.get(type).valueChanges
                .pipe(startWith(this.bruteForceInitialValue(type)), pairwise())
                .subscribe(([prev, next]: [any, any]) => {
                    this.bruteForceAutoCompleteValue(type, next);
                    this.forcedInputs[type] = false;
                });
        });

    }

    // * Autocomplete.
    bruteForceAutoCompleteValue(type, value) {
        if (!this.forcedInputs[type]) {
            this.forcedInputs[type] = true;
            this.detailsForm.patchValue({[type]: value});
        }
        this.userSelectionsService.set('pickupLocation', this.detailsForm.value);
    }

    // * Autocomplete.
    bruteForceInitialValue(type) {
        if (
            this.userSelectionsService.get('pickupLocation') &&
            this.userSelectionsService.get('pickupLocation')[type]) {
            return this.userSelectionsService.get('pickupLocation')[type];
        } else {
            return '';
        }
    }

    radioChanged(event) {
        this.showAddress = event.radioOption === this.utilities.startLocationAsString('off');
        this.isOffSite.emit(this.showAddress);
    }
}
